import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router';
import MoonLoader from 'react-spinners/MoonLoader'
import useClinics from '../../store/clinics/clinicsContext'
import { useTable, useGlobalFilter, usePagination, useFilters, useSortBy } from 'react-table'
import { COLORS } from '../../config';
import SecondaryButton from '../../components/Buttons/SecondaryButton';
import SecondaryButtonAccent from '../../components/Buttons/SecondaryButtonAccent';
import PlusIcon from '../../components/Icons/solid/PlusIcon';
import AdjustmentsIcon from '../../components/Icons/solid/AdjustmentsIcon';
import DotsIcon from '../../components/Icons/solid/DotsIcon';
import FiltersModal from '../ReactTable/FiltersModal';
import GlobalFilter from '../ReactTable/GlobalFilter';
import TablePagination from '../ReactTable/TablePagination';
import SelectColumnFilter from '../ReactTable/SelectColumnFilter';
import SortAscSmall from '../../components/Icons/solid/SortAscSmall';
import SortDescSmall from '../../components/Icons/solid/SortDescSmall';
import TableRowSubMenu from '../../components/Menus/TableRowSubMenu';
import OfficeIcon from '../../components/Icons/outline/OfficeIcon';
import useOnClickOutside from '../../hooks/useOnClickOutside';

function ClinicList() {
  
  const history = useHistory();
  const ref = useRef();

  const {
    clinicsState,
    getAllClinics,
  } = useClinics();

  const [isLoading, setIsLoading] = React.useState(true);
  const [showSubMenu, setShowSubMenu] = React.useState({
    active: false,
    row: null
  })

  useOnClickOutside(
    ref,
    setShowSubMenu.bind(this, {
      active: false,
      row: null,
    })
  );

  
  useEffect(() => {
    getAllClinics().then(() => setIsLoading(false));
  
  }, []);

  const allClinics = clinicsState.allClinics;

  const [showFilters, setShowFilters] = React.useState(false)

  const data = React.useMemo(
    () => []
  
  , [allClinics]);

  if (allClinics.length && !data.length) {
    allClinics.map(clinic => (
      data.push({
        name: clinic.name,
        type: clinic.type,
        email: clinic.email,
        city: clinic.location.city,
        country: clinic.location.country,
      })
    ))
  };

  const defaultColumn = React.useMemo(() => {
    return {
      Filter: SelectColumnFilter
    }
  }, [])

  const columns = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        disableFilters: true,
      },
      {
        Header: 'Type',
        accessor: 'type',
      },
      {
        Header: 'Email',
        accessor: 'email',
        disableFilters: true,
      },
      {
        Header: 'City',
        accessor: 'city',
      },
      {
        Header: 'Country',
        accessor: 'country',
      },
    ], []
  );

  
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
    gotoPage,
    prepareRow,
    state : { globalFilter, pageIndex, pageSize },
    preGlobalFilteredRows,
    setGlobalFilter,
    setAllFilters
  } = useTable(
    {
      columns,
      data,
      defaultColumn
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handleRowSubMenuClick = (row) => {
    setShowSubMenu({
      active: !showSubMenu.active,
      row: row
    })
  }

  const handleViewDetails = (rowIndex) => {
    history.push('/manage-clinics/' + allClinics[rowIndex]._id)
    setShowSubMenu({
      active: false,
      row: null
    })
  }

  return (
    <div className='w-full rounded bg-white shadow'>
      <FiltersModal
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        headerGroups={headerGroups}
        setAllFilters={setAllFilters}
      />
      <div className='p-2 xl:p-4 grid grid-cols-1 gap-y-2 lg:flex lg:items-center w-full'>
        <SecondaryButtonAccent onClick={() => history.push('/manage-clinics/add')} text="Add Clinic" icon={<PlusIcon fill={COLORS.colorPrimary}/>}/>
        <SecondaryButton onClick={() => setShowFilters(true)} text="Set filters" className='lg:ml-auto lg:mr-2' icon={<AdjustmentsIcon fill={COLORS.colorSecondary}/>}/>
        <div>
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </div>
      </div>
      {isLoading && 
      <div className='flex justify-around min-h-170 pt-8'>
        <MoonLoader color={COLORS.colorPrimary}/>
      </div> 
      }
      {!isLoading &&
      <div className='overflow-x-scroll'>
        <table {...getTableProps()} className='w-full mt-4 border-4 border-transparent'>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                <th className='text-left xl:px-2 pb-3 border-b-2 border-gray-100 '/>
                {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())} className='text-secondary xl:px-2 pb-3 border-b-2 border-gray-100 text-left text-base font-semibold'>
                  <span className='flex items-center'>
                  {column.render('Header')}
                  {column.isSorted ? 
                    column.isSortedDesc ? 
                      
                      <SortAscSmall style={'ml-1 h-5 w-5'}/>
                      
                      : <SortDescSmall style={'ml-1 h-5 w-5'} />
                  : ''}
                  </span>
                </th>
                ))}
                <th className='text-left xl:px-2 pb-3 border-b-2 border-gray-100 '></th>
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className=''>
            {page.map(row => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()} className={`border-b-2 border-gray-100`}>
                  <td  className='text-secondary font-medium xl:px-2 py-3'/>
                  
                  {row.cells.map(cell => {
                    if (cell.column.id === 'name') {
                      return (
                        <td {...cell.getCellProps()} className='text-secondary font-medium xl:px-2 py-3'>
                        {cell.render('Cell')}
                        </td>
                      )
                    };
                    if (cell.column.id === 'city') {
                      return (
                        <td {...cell.getCellProps()} className='text-gray-500 font-medium xl:px-2 py-3'>
                        {cell.render('Cell')}
                        </td>
                      )
                    };
                    return (
                      <td {...cell.getCellProps()} className='text-gray-500  xl:px-2 py-3'>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                  <td className='text-left xl:px-2 relative'>
                    <div ref={ref} className='cursor-pointer flex flex-row-reverse '>
                      <DotsIcon vertical fill={COLORS.colorShade} onClick={() => handleRowSubMenuClick(row.index)}/>
                    </div>
                    {showSubMenu.active && showSubMenu.row === row.index &&
                    <TableRowSubMenu>
                      <ul className='whitespace-nowrap'>
                        <li className='cursor-pointer flex items-center' onClick={() => handleViewDetails(row.index)}>
                          <OfficeIcon size='w-5 h-5' stroke={COLORS.colorPrimary}/>
                          <span className='ml-3'>View clinic details</span>
                        </li>
                      </ul>
                    </TableRowSubMenu>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      }
      <TablePagination   
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        previousPage={previousPage}
        nextPage={nextPage}
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        setPageSize={setPageSize}
        pageSize={pageSize}
        gotoPage={gotoPage}
      />
    </div>
  )
}

export default ClinicList;
